import { useState, useMemo } from 'react'
import {
    Cell,
    DataTableObjectType,
    addLinkToFirstColumn,
    buildDataTableObject,
    insertRows,
} from 'utils/data'
import {
    usePayrollLocationDaily,
    usePayrollLocationWeekly,
} from 'hooks/payroll/location'
import { BasicPlaceholder } from 'components/placeholder'
import { isStore } from 'utils/location'
import {
    getLocationTableIndexes,
    PAYROLL_TIME_DIMENSIONS,
    pickData,
} from '../helpers'
import { extendWidthsForExtraColumns } from 'utils/table'
import { Table } from 'components/table-compat/table-compat'
import { ErrorAlert } from 'components/error-fallback'
import { useEssentials } from 'hooks/utils/useEssentials'
import { TRANSLATION_KEYS } from '../../../locales/translation-keys'
import { translateTableData } from '../../../locales/utils'

interface IStoreLocation {
    currentLocation: string
    isLoading: boolean
    bottomLeftCell: string
    count: number
    activeTimePeriod: number
}

export const StoreLocation = ({
    isLoading,
    currentLocation,
    bottomLeftCell,
    count,
    activeTimePeriod,
}: IStoreLocation) => {
    const { payroll } = TRANSLATION_KEYS
    const { location } = payroll
    const { env, session, t, isEnglish } = useEssentials()

    const timeDimension = PAYROLL_TIME_DIMENSIONS[activeTimePeriod]
    const isNextMonth = timeDimension === 'next_month'
    const [selectedWeekByTimePeriod, setSelectedWeekByTimePeriod] = useState<
        Partial<Record<(typeof PAYROLL_TIME_DIMENSIONS)[number], string>>
    >({})
    const effectiveDate = selectedWeekByTimePeriod[timeDimension] ?? ''

    /* Store-level weekly data — all three fetched eagerly for instant tab switching */
    let {
        data: weeklyData,
        refetch: weeklyRefetch,
        error: weeklyError,
        isError: isWeeklyError,
    } = usePayrollLocationWeekly({
        env,
        accessToken: session?.accessToken ?? '',
        location: currentLocation,
        timeDimension: 'this_month',
    })

    let {
        data: priorMonthWeeklyData,
        refetch: priorMonthWeeklyRefetch,
        error: priorMonthWeeklyError,
        isError: isPriorMonthWeeklyError,
    } = usePayrollLocationWeekly({
        env,
        accessToken: session?.accessToken ?? '',
        location: currentLocation,
        timeDimension: 'prior_month',
    })

    let {
        data: nextMonthWeeklyData,
        refetch: nextMonthWeeklyRefetch,
        error: nextMonthWeeklyError,
        isError: isNextMonthWeeklyError,
    } = usePayrollLocationWeekly({
        env,
        accessToken: session?.accessToken ?? '',
        location: currentLocation,
        timeDimension: 'next_month',
    })

    const translateWeeklyData = (data: typeof weeklyData) => {
        if (!data) return data
        return translateTableData({
            t,
            table: data,
            translation: {
                'plan hrs': payroll.planHrs,
                'act+rem sched hrs': payroll.actRemSchedHrs,
                'Actual + Remaining Wkly': payroll.actRemSchedHrs,
                variance: location.variance,
            },
        })
    }

    if (weeklyData?.rows) {
        weeklyData = {
            ...weeklyData,
            rows: addLinkToFirstColumn([...weeklyData.rows]),
        }
        weeklyData = translateWeeklyData(weeklyData)
    }

    if (priorMonthWeeklyData?.rows) {
        priorMonthWeeklyData = {
            ...priorMonthWeeklyData,
            rows: addLinkToFirstColumn([...priorMonthWeeklyData.rows]),
        }
        priorMonthWeeklyData = translateWeeklyData(priorMonthWeeklyData)
    }

    // Next month: only plan hrs, scheduled hrs, and variance columns are meaningful.
    // Daily drill is not available for a future month, so we don't add links.
    if (nextMonthWeeklyData?.rows) {
        nextMonthWeeklyData = {
            ...nextMonthWeeklyData,
            rows: nextMonthWeeklyData.rows,
        }
        nextMonthWeeklyData = translateWeeklyData(nextMonthWeeklyData)
    }

    const currentWeeklyData =
        timeDimension === 'prior_month'
            ? priorMonthWeeklyData
            : timeDimension === 'next_month'
              ? nextMonthWeeklyData
              : weeklyData
    const currentWeeklyError =
        timeDimension === 'prior_month'
            ? priorMonthWeeklyError
            : timeDimension === 'next_month'
              ? nextMonthWeeklyError
              : weeklyError
    const isCurrentWeeklyError =
        timeDimension === 'prior_month'
            ? isPriorMonthWeeklyError
            : timeDimension === 'next_month'
              ? isNextMonthWeeklyError
              : isWeeklyError
    const currentWeeklyRefetch =
        timeDimension === 'prior_month'
            ? priorMonthWeeklyRefetch
            : timeDimension === 'next_month'
              ? nextMonthWeeklyRefetch
              : weeklyRefetch

    const baseWidths = isEnglish ? [85, 70, 75, 70] : [65, 60, 125, 70]
    const columnCount = currentWeeklyData?.headings?.length ?? baseWidths.length
    const widths = extendWidthsForExtraColumns(baseWidths, columnCount, 75)

    /* Daily data — fetched only when a week is clicked (GF card 539882).
       Not applicable for next month — daily adjustments and actual hrs are unavailable. */
    const {
        data: allDailyData,
        error: dailyError,
        isError: isDailyError,
        refetch: dailyRefetch,
    } = usePayrollLocationDaily({
        env,
        accessToken: session?.accessToken ?? '',
        location: currentLocation,
        timeDimension,
        drillEnabled: !isNextMonth && effectiveDate.length > 0,
    })

    // Filter daily rows to the clicked week by matching
    // week_end_date (daily col [2]) to the weekly row date (currentDate)
    const filteredDailyData = useMemo(() => {
        if (isNextMonth || !allDailyData?.rows || !effectiveDate) {
            return undefined
        }
        const filteredRows = allDailyData.rows.filter(
            (row) => row[2]?.value === effectiveDate,
        )
        if (filteredRows.length === 0) return undefined
        return { ...allDailyData, rows: filteredRows }
    }, [allDailyData, effectiveDate, isNextMonth])

    let translatedDailyData = filteredDailyData
    if (translatedDailyData) {
        translatedDailyData = translateTableData({
            t,
            table: translatedDailyData,
            translation: {
                'plan hrs': payroll.planHrs,
                'act+rem sched hrs': payroll.actRemSchedHrs,
                'Actual + Remaining Wkly': payroll.actRemSchedHrs,
                variance: location.variance,
            },
        })
    }

    if (isLoading || !currentWeeklyData) {
        return (
            <div className="placeholder-container" data-testid="placeholder">
                <BasicPlaceholder type="table" rows={5} columns={5} />
            </div>
        )
    }
    if (isCurrentWeeklyError) {
        return (
            <ErrorAlert
                error={currentWeeklyError}
                count={count}
                onRetry={() => currentWeeklyRefetch()}
            />
        )
    }

    if (isDailyError) {
        return (
            <ErrorAlert
                error={dailyError}
                count={count}
                onRetry={() => dailyRefetch()}
            />
        )
    }

    const handleClick = (clickedDate: string) => {
        // Daily drill is disabled for next month — no-op
        if (isNextMonth) return
        const matchedObject = currentWeeklyData?.rows.find(
            (row: Cell[]) => row[0].value === clickedDate,
        )
        const matchedDate = matchedObject?.[3]?.value
        if (matchedDate === effectiveDate) {
            setSelectedWeekByTimePeriod((prev) => ({
                ...prev,
                [timeDimension]: '',
            }))
        } else if (matchedDate) {
            setSelectedWeekByTimePeriod((prev) => ({
                ...prev,
                [timeDimension]: matchedDate,
            }))
        }
    }

    let displayData = currentWeeklyData
        ? { ...currentWeeklyData }
        : currentWeeklyData

    if (displayData) {
        displayData.rows = (displayData.rows ?? []).filter(
            (row) => !row[0]?.childOf,
        )
        if (translatedDailyData && displayData) {
            const updatedWeeklyData = { ...displayData }
            const { rows } = updatedWeeklyData
            const updatedDailyData = { ...translatedDailyData }
            if (updatedDailyData.rows) {
                updatedDailyData.rows = updatedDailyData.rows.map(
                    (row: Cell[]) => [
                        { ...row[0], childOf: effectiveDate, link: undefined },
                        ...row.slice(1),
                    ],
                )
                const insertIndex =
                    rows.findIndex((row) => row[3]?.value === effectiveDate) + 1
                insertRows(
                    updatedWeeklyData.rows,
                    insertIndex,
                    updatedDailyData.rows,
                )
                displayData = updatedWeeklyData
            }
        }
    }

    if (!displayData) {
        return (
            <div className="placeholder-container" data-testid="placeholder">
                <BasicPlaceholder type="table" rows={5} columns={5} />
            </div>
        )
    }

    const { startIndex: tableStartIndex, endIndex: tableEndIndex } =
        getLocationTableIndexes(currentLocation)

    const hasData =
        (displayData?.rows && displayData.rows.length > 0) ||
        (displayData?.footer && displayData.footer.length > 0)

    if (!displayData || !hasData) {
        return <ErrorAlert variant="no-data" count={count} />
    }

    const objParams = {
        currentData: pickData(
            displayData,
            currentLocation,
            tableStartIndex,
            tableEndIndex,
            t(payroll.weekNo),
        ),
        widths,
        bottomLeftCell,
        pinFirstColumn: true,
    } as DataTableObjectType
    // Next month: no daily drill — rows are not clickable
    if (isStore(currentLocation) && !isNextMonth) {
        objParams.handleClick = handleClick
    }
    const obj = buildDataTableObject(objParams)

    return <Table data={obj} scrollableWidth={true} />
}
